import { PartyVitalsState, type PartyVitalsRecord } from "odyssey-shared";

/**
 * What the frames hold of each member's vitals (ADR-101 decisions 20 and 23): the `partyVitals` frame's members by
 * roster place, and the player's own, which the frame never carries, from the HUD's own `vitals`.
 */

/**
 * A member's vitals as a frame draws them: health in 255ths of its maximum (0 only when dead), the flag byte the
 * flag mark reads (the state in bits 0 and 1, the zone lock in bit 7 for the player's own) and whether it is dead.
 */
export interface MemberVitals {
  readonly health: number;
  readonly flag: number;
  readonly dead: boolean;
}

/**
 * The vitals of a frame that holds none, one map for every such frame.
 */
export const NO_VITALS: ReadonlyMap<number, MemberVitals> = new Map();

/**
 * The most a health byte holds.
 */
const FULL = 255;

/**
 * Health in 255ths of the maximum as the map server writes it: rounded down, at least 1 while alive and 0 when dead.
 *
 * @param health The health.
 * @param max The maximum health.
 * @param dead Whether the character is dead.
 * @returns The byte; 0 for a dead character or a maximum of 0.
 */
export function healthByte(health: number, max: number, dead: boolean): number {
  if (dead || !(max > 0)) {
    return 0;
  }
  return Math.min(FULL, Math.max(1, Math.floor((FULL * health) / max)));
}

/**
 * The members of a decoded `partyVitals` by roster place.
 *
 * @param record The decoded frame; only its first `count` entries are read.
 * @returns The members, or `NO_VITALS` for a frame of none.
 */
export function memberVitalsOf(record: Readonly<PartyVitalsRecord>): ReadonlyMap<number, MemberVitals> {
  if (record.count === 0) {
    return NO_VITALS;
  }
  const members = new Map<number, MemberVitals>();
  for (let index = 0; index < record.count; index += 1) {
    const state = record.states[index] as number;
    members.set(record.places[index] as number, { health: record.healths[index] as number, flag: state & PartyVitalsState.FlagMask, dead: (state & PartyVitalsState.Dead) !== 0 });
  }
  return members;
}

/**
 * The player's own vitals for their frame.
 *
 * @param own The HUD's health, maximum and dead state, or null before the first `vitals`.
 * @param flag The HUD's flag byte.
 * @returns The vitals, or null before the first `vitals`.
 */
export function ownVitalsOf(own: { readonly health: number; readonly maxHealth: number; readonly dead: boolean } | null, flag: number): MemberVitals | null {
  return own === null ? null : { health: healthByte(own.health, own.maxHealth, own.dead), flag, dead: own.dead };
}

/**
 * Whether two members' vitals are equal.
 *
 * @param a One.
 * @param b The other.
 * @returns True when health, flag and dead are all equal.
 */
export function sameMember(a: MemberVitals, b: MemberVitals): boolean {
  return a.health === b.health && a.flag === b.flag && a.dead === b.dead;
}

/**
 * Whether a decoded frame holds exactly what a map holds, read straight from the record so a repeated frame builds
 * nothing.
 *
 * @param record The decoded frame.
 * @param held The map.
 * @returns True when the same places carry the same values.
 */
export function recordMatches(record: Readonly<PartyVitalsRecord>, held: ReadonlyMap<number, MemberVitals>): boolean {
  if (record.count !== held.size) {
    return false;
  }
  for (let index = 0; index < record.count; index += 1) {
    const state = record.states[index] as number;
    const member = held.get(record.places[index] as number);
    if (member === undefined || member.health !== record.healths[index] || member.flag !== (state & PartyVitalsState.FlagMask) || member.dead !== ((state & PartyVitalsState.Dead) !== 0)) {
      return false;
    }
  }
  return true;
}

/**
 * Whether two sets of vitals hold the same members with the same values, so a frame that repeats the last is not a
 * change.
 *
 * @param a One set.
 * @param b The other.
 * @returns True when equal.
 */
export function sameVitals(a: ReadonlyMap<number, MemberVitals>, b: ReadonlyMap<number, MemberVitals>): boolean {
  if (a === b) {
    return true;
  }
  if (a.size !== b.size) {
    return false;
  }
  for (const [place, left] of a) {
    const right = b.get(place);
    if (right === undefined || !sameMember(left, right)) {
      return false;
    }
  }
  return true;
}
